<template>
  <div
    class="absolute w-full h-full rounded-[2rem] overflow-hidden top-0 left-0"
    :class="{ shimmerElement: deal?.shimmer }"
    :style="{
      backgroundColor: deal?.shimmer
        ? 'transparent'
        : deal?.dealBgColor || dealsColors[position % 4]
    }"
  >
    <div
      class="absolute -skew-x-12 h-full w-2/3 right-0 translate-x-1/4 overflow-auto opacity-80"
      :class="{
        'bg-white/20': position % 2 == 0,
        'bg-black/40': position % 2 == 1
      }"
    >
      <div class="w-[120%] h-full right-0 skew-x-12 absolute">
        <ImgComponent
          :src="
            deal?.image ||
            deal?.principalImageUrl ||
            deal?.targetData?.[0]?.principalImageUrl ||
            deal?.targetData?.[0]?.imageUrl ||
            deal?.restaurantLogo ||
            deal?.restaurant?.logo ||
            '/images/logo.png'
          "
          class="w-full h-full object-cover bg-white"
          alt=""
        />
      </div>
    </div>
    <div class="grid grid-cols-2 w-full h-full text-white relative">
      <div class="flex flex-col justify-between p-6">
        <div class="text-xs uppercase tracking-wide opacity-80">
          Offre spéciale
        </div>
        <div class="flex items-baseline gap-1">
          <template
            v-if="
              deal?.discountType === 'percentage' ||
              deal?.discountType === 'percent'
            "
          >
            <span
              class="font-bold"
              :class="{ 'text-2xl': small, 'text-3xl': !small }"
              >-{{ deal?.discountValue || deal?.discount }}</span
            >
            <span
              class="font-medium"
              :class="{ 'text-sm': small, 'text-xl': !small }"
              >%</span
            >
            <span
              class="opacity-80 font-medium ml-1 uppercase truncate"
              :class="{ 'text-xs': small, 'text-sm': !small }"
              >réduction</span
            >
          </template>
          <template v-else-if="deal?.discountType === 'fixed'">
            <span
              class="font-bold"
              :class="{ 'text-2xl': small, 'text-3xl': !small }"
              >-{{ deal?.discountValue }}</span
            >
            <span
              class="font-medium"
              :class="{ 'text-sm': small, 'text-xl': !small }"
              >F</span
            >
            <span
              :class="{ 'text-xs': small, 'text-sm': !small }"
              class="opacity-80 font-medium ml-1 uppercase truncate"
              >réduction</span
            >
          </template>
          <template v-else>
            <span class="text-2xl font-bold">Offre</span>
          </template>
        </div>
        <div
          class="line-clamp-2 font-semibold leading-tight mt-1"
          :class="{ 'text-sm': small, 'text-lg': !small }"
        >
          {{ dealTitle }}
        </div>
        <!-- Description removed for cleaner UI as per user request -->
        <div
          class="drop-shadow-lg drop-shadow-black/80 truncate text-primary-400 mt-1"
          :class="{ 'text-xs mb-1': small, 'text-sm mb-2': !small }"
        >
          {{
            deal?.restaurantName ||
            deal?.restaurant?.storeName ||
            'Restaurant partenaire'
          }}
        </div>
        <div class="">
          <button class="buttonComponent text-sm px-6 py-4!" @click="order">
            Voir l'offfre
          </button>
        </div>
      </div>
      <div class="h-full w-full p-6"></div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { bus } from '@core/events/eventSystem';

const props = defineProps({
  deal: {
    type: Object,
    default: null
  },
  small: {
    type: Boolean,
    default: false
  },
  position: {
    type: Number,
    default: 0
  }
});

const dealsColors = ['#0E1B25', '#9C1715', '#193244', '#18222A'];
const dealsDescriptions = [
  'Ne ratez pas cette opportunité',
  'Faites plaisir à votre langue',
  'Vous êtes le roi, le meilleur pour vous',
  "N'hésitez pas, commander maintenant"
];

import { useRouter } from 'vue-router';

const dealTitle = computed(() => {
  if (props.deal?.title) return props.deal.title;

  const type = props.deal?.targetType;
  const targetData = props.deal?.targetData || [];

  if (type === 'dish') {
    if (targetData.length === 1) {
      return `Offre sur ${targetData[0].name}`;
    } else if (targetData.length == 2) {
      return `Offre sur ${targetData[0].name} et ${targetData[1].name}`;
    } else if (targetData.length > 2) {
      return `Offre sur ${targetData[0].name} et ${targetData.length - 1} autres plats`;
    }
    return 'Offre sur des plats sélectionnés';
  } else if (type === 'category') {
    if (targetData.length === 1) {
      return `Menu ${targetData[0].name}`;
    } else if (targetData.length == 2) {
      return `Menu ${targetData[0].name} et ${targetData[1].name}`;
    } else if (targetData.length > 2) {
      return `Menu ${targetData[0].name} et ${targetData.length - 1} autres catégories`;
    }
    return 'Offre spéciale sur menu';
  } else if (type === 'delivery') {
    return 'Réduction sur la livraison';
  }

  return 'Offre spéciale';
});

const router = useRouter();

// Handle order button click
const order = async () => {
  if (!props.deal) {
    console.error('Invalid deal for ordering');
    return;
  }

  // Case 1: Dish Promotion -> Show specific dish
  if (
    props.deal.targetType === 'dish' &&
    props.deal.dishIds &&
    props.deal.dishIds.length > 0
  ) {
    // If it's a single dish, show detail
    if (props.deal.dishIds.length === 1) {
      bus.emit('dish.SHOW_DISH_DETAIL', {
        id: props.deal.dishIds[0]
      });
      return;
    }
  }

  // Case 2: Categories or Delivery or Multiple Dishes -> Go to deal detail page
  if (props.deal.id) {
    router.push({
      name: 'deal-detail',
      params: { id: props.deal.id }
    });
    return;
  }

  // Fallback: If for some reason we only have restaurantId
  if (props.deal.restaurantId) {
    bus.emit('search:by_restaurant_requested', {
      id: props.deal.restaurantId,
      name: props.deal.restaurantName
    });
    return;
  }
};
</script>
